import { Injectable } from '@angular/core';
import { SafariViewController, SafariViewControllerOptions } from '@awesome-cordova-plugins/safari-view-controller/ngx';
import { Capacitor } from '@capacitor/core';
import { Subscription } from 'rxjs';

/**
 * This ends up being a pass-through service for working with the Safari View Controller Cordova plugin.
 */
@Injectable({
  providedIn: 'root',
})
export class SafariViewWrapperService {
  private safariViewController?: SafariViewController;
  constructor() {
    this.create();
  }

  private canUsePlugin(): boolean {
    return typeof window !== 'undefined' && Capacitor.isNativePlatform();
  }

  async create(): Promise<SafariViewController | undefined> {
    if (!this.canUsePlugin()) {
      return undefined;
    }

    try {
      this.safariViewController = new SafariViewController();
      await this.safariViewController.connectToService();
      return this.safariViewController;
    } catch {
      return undefined;
    }
  }

  async get(): Promise<SafariViewController | undefined> {
    if (!this.safariViewController) {
      return this.create();
    }
    return this.safariViewController;
  }

  async show(options?: SafariViewControllerOptions): Promise<any> {
    return (await this.get())?.show(options);
  }

  async closeAll(): Promise<any> {
    return (await this.get())?.hide();
  }

  async isAvailable(): Promise<boolean> {
    const controller = await this.get();
    if (!controller) {
      return false;
    }

    try {
      return await controller.isAvailable();
    } catch {
      return false;
    }
  }

  async launchBrowserUI(url: string, next?: (data: any) => any): Promise<Subscription | undefined> {
    return (await this.get())
      ?.show({
        url,
        hidden: false,
        animated: false,
        transition: 'curl',
        enterReaderModeIfAvailable: true,
        tintColor: '#ff0000',
      })
      .subscribe({
        next,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: (error: any) => {
          console.error('SafariViewController Error', error);
        },
      });
  }
}
